import {Alert, Autocomplete, Box, Checkbox, Chip, Divider, FormControlLabel, IconButton, InputAdornment, MenuItem, Select, Stack, Switch, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useActiveServices, useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useAllServiceCategories} from "crqlar-react-common/dist/utils/hooks/services/useAllServiceCategories";
import {
    BOOKING_ENGINE_OPTIONAL_FIELDS,
    BookingEngine, EARLIEST_BOOKABLE_SLOT_FROM_NOW_IN_HOURS,
    OptionalFieldConfig,
    PaymentRequirement,
    TIMESLOT_DURATION_MINUTES
} from "crqlar-react-common/dist/models/bookingEngine/bookingEngine.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useQuery} from "react-query";
import {PaymentMethodService} from "crqlar-react-common/dist/services/billing/paymentMethod.service";
import {AvailabilityCalculationService} from "crqlar-react-common/dist/services/availability/availabilityCalculation.service";
import {Datepicker} from "@mobiscroll/react";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ButtonBase from "@mui/material/ButtonBase";
import {useNavigate} from "react-router-dom";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";

interface Props {
    bookingEngine: BookingEngine;
    setBookingEngine: (bookingEngine: BookingEngine) => void;
    nameMissing: boolean;
    focusNameOnLoad: boolean;
}

const SectionBox = ({
                        title,
                        toggleable = false,
                        expanded = true,
                        onToggle,
                        children,
                    }: {
    title: string;
    toggleable?: boolean;
    expanded?: boolean;
    onToggle?: (val: boolean) => void;
    children: React.ReactNode;
}) => {
    const titleElement = <Typography variant="h6">{title}</Typography>;
    const headerSx = {
        width: '100%',
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        textAlign: 'left',
    } as const;

    return (
        <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden'}}>
            {toggleable ? (
                <ButtonBase
                    aria-expanded={expanded}
                    onClick={() => onToggle?.(!expanded)}
                    sx={headerSx}
                >
                    {titleElement}
                    <ArrowDownwardIcon
                        sx={{
                            flexShrink: 0,
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: theme => theme.transitions.create('transform', {
                                duration: theme.transitions.duration.shortest,
                            }),
                        }}
                    />
                </ButtonBase>
            ) : (
                <Box sx={headerSx}>
                    {titleElement}
                </Box>
            )}

            {expanded && (
                <Box sx={{px: 2, pt: 1, pb: 2}}>
                    {children}
                </Box>
            )}
        </Box>
    );
};

const InfoTooltip = ({ title }: { title: string }) => (
    <Tooltip
        title={title}
        placement="right"
        componentsProps={{ tooltip: { sx: { fontSize: '0.85rem' } } }}
    >
        <InfoOutlinedIcon sx={{ color: 'info.main', cursor: 'help', ml: 0.5, fontSize: '1.2rem', verticalAlign: 'middle' }} />
    </Tooltip>
);

const BookingEngineBasicTab = ({
                                   bookingEngine,
                                   setBookingEngine,
                                   nameMissing,
                                   focusNameOnLoad,
                               }: Props) => {

    const {t} = useLocalize()
    const navigate = useNavigate()
    // Read-only users may browse every booking-engine setting (and still open/close the
    // collapsible sections) but must not edit anything. Property-wrapped fields are already
    // locked by the shared Property component; here we additionally disable the controls that
    // live outside Property (checkboxes, the time-frame switch, Mobiscroll pickers, count
    // fields) plus the MUI Selects/Autocomplete (which `inert` does not reliably block). The
    // SectionBox expand/collapse toggles are intentionally left interactive.
    const readOnly = useReadOnly()
    const allServices = useAllServices()
    const activeServices = useActiveServices()
    const allSectors = useAllSectors()
    const serviceCategories = useAllServiceCategories()

    const serviceCategoryMap = useMemo(() => {
        const map = new Map<string, string>()
        serviceCategories.forEach(cat => { if (cat.id) map.set(cat.id, cat.name || t('Uncategorized')) })
        return map
    }, [serviceCategories])

    const sectorMap = useMemo(() => {
        const map = new Map<string, string>()
        allSectors.forEach(s => { if (s.id) map.set(s.id, s.name || t('Other')) })
        return map
    }, [allSectors])

    const getServiceCategoryName = (service: any): string => {
        const firstCategoryId = service.categoryIds?.[0]
        if (!firstCategoryId) return t('Uncategorized')
        return serviceCategoryMap.get(firstCategoryId) || t('Uncategorized')
    }

    const getSectorName = (service: any): string => {
        const firstSectorId = service.sectorIds?.[0]
        if (!firstSectorId) return t('Other')
        return sectorMap.get(firstSectorId) || t('Other')
    }

    const sortedServices = useMemo(() => {
        return [...activeServices].sort((a, b) => {
            const sectorCmp = getSectorName(a).localeCompare(getSectorName(b))
            if (sectorCmp !== 0) return sectorCmp
            const catCmp = getServiceCategoryName(a).localeCompare(getServiceCategoryName(b))
            if (catCmp !== 0) return catCmp
            return (a.name || '').localeCompare(b.name || '')
        })
    }, [activeServices, allServices, sectorMap, serviceCategoryMap])

    const getDisplayCategoryName = (option: any): string => {
        if (!option._displayCategoryId) return t('Uncategorized')
        return serviceCategoryMap.get(option._displayCategoryId) || t('Uncategorized')
    }

    const expandedSortedServices = useMemo(() => {
        const expanded: any[] = []
        for (const service of sortedServices) {
            if (!service.categoryIds || service.categoryIds.length === 0) {
                expanded.push({ ...service, _displayCategoryId: undefined })
            } else {
                for (const catId of service.categoryIds) {
                    expanded.push({ ...service, _displayCategoryId: catId })
                }
            }
        }
        return expanded
    }, [sortedServices])

    const availableCardProvidersQ = useQuery(['availableCardProviders'], async () => {
        return await PaymentMethodService.getAvailableCardProviders()
    })
    const availableCardProviders = availableCardProvidersQ.data || []

    // Gets the availabilities of all resources attached to service
    // Then determines whether there are availabilities which are longer that service duration period
    const selectedServiceIds = bookingEngine.serviceIds || []
    const serviceAvailabilityStatusQ = useQuery(
        ['serviceAvailabilityStatus', selectedServiceIds],
        async () => {
            return await AvailabilityCalculationService.getServiceAvailabilityStatus({ serviceIds: selectedServiceIds })
        },
        { enabled: selectedServiceIds.length > 0 }
    )
    const serviceAvailabilityStatuses = serviceAvailabilityStatusQ.data?.statuses || {}

    const updateField = (field: keyof BookingEngine, value: any) => {
        setBookingEngine({...bookingEngine, [field]: value})
    }

    const hasPackageService = !!allServices?.find(s => s.isPackage && bookingEngine.serviceIds?.find(bookingServiceId => bookingServiceId === s.id))

    useEffect(() => {
        if (hasPackageService) {
            setBookingEngine({
                ...bookingEngine,
                cartConfig: {
                    ...bookingEngine.cartConfig,
                    allowMultipleRes: false
                }
            })
        }
    }, [hasPackageService]);

    const isRestaurantSector = allSectors.find(s => s.id === bookingEngine.sectorId)?.sectorType === SectorType.RESTAURANT

    const [paymentExpanded, setPaymentExpanded] = useState(false)
    const [advancedExpanded, setAdvancedExpanded] = useState(false)
    const nameInputRef = useRef<HTMLInputElement>(null)
    const paymentExpandedInitialized = useRef(false)
    const advancedExpandedInitialized = useRef(false)

    useEffect(() => {
        if (focusNameOnLoad) {
            nameInputRef.current?.focus()
        }
    }, [focusNameOnLoad])

    useEffect(() => {
        if (!paymentExpandedInitialized.current && bookingEngine.billingConfig?.paymentRequirement != null) {
            setPaymentExpanded(true)
            paymentExpandedInitialized.current = true
        }
    }, [bookingEngine.billingConfig?.paymentRequirement])

    useEffect(() => {
        if (!advancedExpandedInitialized.current && (
            bookingEngine.formConfig?.peopleLimitEnabled ||
            bookingEngine.formConfig?.serviceRateMandatory ||
            bookingEngine.cartConfig?.allowMultipleRes ||
            bookingEngine.formConfig?.timeSelection === false
        )) {
            setAdvancedExpanded(true)
            advancedExpandedInitialized.current = true
        }
    }, [
        bookingEngine.formConfig?.peopleLimitEnabled,
        bookingEngine.formConfig?.serviceRateMandatory,
        bookingEngine.cartConfig?.allowMultipleRes,
        bookingEngine.formConfig?.timeSelection,
    ])

    // ── Basic Info ───────────────────────────────────────────────────────────

    // Tracks which sector header was last rendered during a single render pass
    // so we can show the sector header only once per sector in the grouped dropdown.
    let _lastRenderedSector: string | null = null

    const basicInfoSection = (
        <>
            <Property title={t('Name')}>
                <TextField
                    id={'name'}
                    inputRef={nameInputRef}
                    size={'small'}
                    value={bookingEngine?.name ?? ''}
                    onChange={(e) => updateField('name', e.target.value)}
                    fullWidth
                    required
                    error={nameMissing}
                    disabled={readOnly}
                />
            </Property>

            <Divider/>

            <Property title={t('Services')}>
                <Stack direction={'column'} gap={1} width={'100%'} alignItems={'flex-start'}>
                    <Stack direction={'row'} gap={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'success.main', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('Service is online bookable')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'error.main', flexShrink: 0 }} />
                            <Typography variant="body2" color="text.secondary">
                                {t('Service is not online bookable')}
                            </Typography>
                        </Box>
                    </Stack>
                    <Alert severity="info" sx={{ width: '100%', fontSize: '0.95rem' }}>
                        {t('service_sector_restriction_notice')}
                    </Alert>
                    {Object.values(serviceAvailabilityStatuses).some(v => v === false) && (
                        <Alert severity="warning" sx={{ width: '100%', fontSize: '0.95rem' }}>
                            {t("There are some services that can't be booked online. Reason: Availabilities in resource may be shorter than service duration.")}
                        </Alert>
                    )}
                    <Autocomplete
                        id={'services'}
                        disabled={readOnly}
                        options={expandedSortedServices}
                        multiple
                        disableCloseOnSelect
                        // Group by composite key "sectorName||categoryName" to support 3-level hierarchy
                        groupBy={(option: any) => `${getSectorName(option)}||${getDisplayCategoryName(option)}`}
                        getOptionLabel={(option: any) => option?.name || ''}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id && option._displayCategoryId === value._displayCategoryId}
                        value={bookingEngine.serviceIds?.flatMap(id => {
                            const matches = activeServices.filter(s => s.id === id)
                            return matches.length > 0 ? matches : []
                        }) || []}
                        filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options
                            const lower = inputValue.toLowerCase()
                            return options.filter((s: any) => {
                                return (s.name || '').toLowerCase().includes(lower)
                                    || getSectorName(s).toLowerCase().includes(lower)
                                    || getDisplayCategoryName(s).toLowerCase().includes(lower)
                            })
                        }}
                        getOptionDisabled={(option: any) => {
                            if (!bookingEngine.sectorId) return false
                            const optionSectorId = option.sectorIds?.[0]
                            return !!optionSectorId && optionSectorId !== bookingEngine.sectorId
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size={'small'} />
                        )}
                        renderTags={(value, getTagProps) => {
                            const seenIds = new Set<string>()
                            const uniqueValue = value.filter((option: any) => {
                                if (!option?.id || seenIds.has(option.id)) return false
                                seenIds.add(option.id)
                                return true
                            })
                            return uniqueValue.map((option: any, index: number) => {
                                const { key, ...tagProps } = getTagProps({ index })
                                const isAvailable = option?.id ? serviceAvailabilityStatuses[option.id] : undefined
                                const chipColor = isAvailable === true ? 'success' : isAvailable === false ? 'error' : 'default'
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        color={chipColor}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>{option?.name || ''}</span>
                                                <Tooltip title={t('Edit service')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(ev) => {
                                                            ev.stopPropagation()
                                                            window.open(`/system/services/${option?.id}`, '_blank')
                                                        }}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <EditIcon sx={{ fontSize: '0.9rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                )
                            })
                        }}
                        fullWidth
                        onChange={(_e, value) => {
                            const newServiceIds = Array.from(new Set(value.map((v: any) => v?.id!)))
                            const newSectorId = newServiceIds.length === 0 ? undefined : (bookingEngine.sectorId || value[0]?.sectorIds?.[0])
                            setBookingEngine({...bookingEngine, serviceIds: newServiceIds, sectorId: newSectorId})
                        }}
                        renderGroup={(params) => {
                            const separatorIndex = params.group.indexOf('||')
                            const sectorName = params.group.substring(0, separatorIndex)
                            const categoryName = params.group.substring(separatorIndex + 2)

                            const isNewSector = sectorName !== _lastRenderedSector
                            _lastRenderedSector = sectorName

                            const selectedIds = new Set(bookingEngine.serviceIds || [])

                            // Category-level toggle
                            const categoryServices = expandedSortedServices.filter(
                                (s: any) => getSectorName(s) === sectorName && getDisplayCategoryName(s) === categoryName
                            )
                            const allCatSelected = categoryServices.length > 0 && categoryServices.every((s: any) => s.id && selectedIds.has(s.id))
                            const someCatSelected = categoryServices.some((s: any) => s.id && selectedIds.has(s.id))

                            const categorySectorId = categoryServices[0]?.sectorIds?.[0]
                            const isCategoryDisabled = !!bookingEngine.sectorId && !!categorySectorId && bookingEngine.sectorId !== categorySectorId

                            const handleCategoryToggle = (e: React.MouseEvent) => {
                                e.stopPropagation()
                                if (isCategoryDisabled) return
                                const currentIds = bookingEngine.serviceIds || []
                                if (allCatSelected) {
                                    const catServiceIds = new Set(categoryServices.map((s: any) => s.id))
                                    const newIds = currentIds.filter(id => !catServiceIds.has(id))
                                    setBookingEngine({...bookingEngine, serviceIds: newIds, sectorId: newIds.length === 0 ? undefined : bookingEngine.sectorId})
                                } else {
                                    const existingSet = new Set(currentIds)
                                    const newIds = categoryServices.filter((s: any) => s.id && !existingSet.has(s.id)).map((s: any) => s.id!)
                                    setBookingEngine({...bookingEngine, serviceIds: [...currentIds, ...newIds], sectorId: bookingEngine.sectorId || categorySectorId})
                                }
                            }

                            // Sector-level toggle (only computed when rendering a new sector)
                            const sectorServices = isNewSector
                                ? sortedServices.filter((s: any) => getSectorName(s) === sectorName)
                                : []
                            const allSectorSelected = isNewSector && sectorServices.length > 0 && sectorServices.every((s: any) => s.id && selectedIds.has(s.id))
                            const someSectorSelected = isNewSector && sectorServices.some((s: any) => s.id && selectedIds.has(s.id))

                            const thisSectorId = sectorServices[0]?.sectorIds?.[0]
                            const isSectorDisabled = isNewSector && !!bookingEngine.sectorId && !!thisSectorId && bookingEngine.sectorId !== thisSectorId

                            const handleSectorToggle = (e: React.MouseEvent) => {
                                e.stopPropagation()
                                if (isSectorDisabled) return
                                const currentIds = bookingEngine.serviceIds || []
                                if (allSectorSelected) {
                                    const sectorServiceIds = new Set(sectorServices.map((s: any) => s.id))
                                    const newIds = currentIds.filter(id => !sectorServiceIds.has(id))
                                    setBookingEngine({...bookingEngine, serviceIds: newIds, sectorId: newIds.length === 0 ? undefined : bookingEngine.sectorId})
                                } else {
                                    const existingSet = new Set(currentIds)
                                    const newIds = sectorServices.filter((s: any) => s.id && !existingSet.has(s.id)).map((s: any) => s.id!)
                                    setBookingEngine({...bookingEngine, serviceIds: [...currentIds, ...newIds], sectorId: bookingEngine.sectorId || thisSectorId})
                                }
                            }

                            return (
                                <li key={params.key}>
                                    {isNewSector && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            sx={{
                                                position: 'sticky',
                                                top: -8,
                                                zIndex: 2,
                                                bgcolor: '#8C8C8C',
                                                px: 1,
                                                py: 0.5,
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={allSectorSelected}
                                                indeterminate={someSectorSelected && !allSectorSelected}
                                                onClick={handleSectorToggle}
                                                disabled={isSectorDisabled}
                                                sx={{
                                                    color: '#fff',
                                                    '&.Mui-checked': { color: '#fff' },
                                                    '&.MuiCheckbox-indeterminate': { color: '#fff' },
                                                }}
                                            />
                                            <Typography variant="body2" fontWeight="bold" sx={{ color: '#fff' }}>
                                                {t('Sector')} {'[' + sectorName + ']'}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{
                                            bgcolor: '#E0E0E0',
                                            px: 1,
                                            py: 0.5,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={allCatSelected}
                                            indeterminate={someCatSelected && !allCatSelected}
                                            onClick={handleCategoryToggle}
                                            disabled={isCategoryDisabled}
                                        />
                                        <Typography variant="body2" fontWeight={500}>
                                            {t('Service Category')} {'[' + categoryName + ']'}
                                        </Typography>
                                    </Box>
                                    <ul style={{ padding: 0, margin: 0, listStyle: 'none', backgroundColor: '#FFFFFF' }}>
                                        {params.children}
                                    </ul>
                                </li>
                            )
                        }}
                    />
                </Stack>
            </Property>
        </>
    )

    // ── Booking Times & Configuration ────────────────────────────────────────

    const bookingTimesSection = (
        <SectionBox title={t('Booking Times & Configuration')}>
            <Stack gap={2}>
                {/* Auto Approve */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'auto-approve'}
                            size={'small'}
                            checked={bookingEngine.confirmationConfig?.autoApprove || false}
                            onChange={(e) => updateField('confirmationConfig', {
                                ...bookingEngine.confirmationConfig,
                                autoApprove: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Auto approve')}</Typography>
                            <InfoTooltip title={t('When enabled, reservations are automatically approved without manual review')} />
                        </Box>
                    }
                />

                {/* Occupancy threshold — restaurant sector only */}
                {isRestaurantSector && (
                    <Property
                        title={t('occupancy_threshold_label')}
                        subtitle={t('occupancy_threshold_tooltip')}
                    >
                        <TextField
                            id={'occupancy-threshold'}
                            type={'number'}
                            size={'small'}
                            fullWidth
                            disabled={readOnly}
                            value={bookingEngine.confirmationConfig?.occupancyThreshold ?? ''}
                            onChange={(e) => {
                                const raw = e.target.value
                                if (raw === '') {
                                    updateField('confirmationConfig', {
                                        ...bookingEngine.confirmationConfig,
                                        occupancyThreshold: undefined
                                    })
                                    return
                                }
                                const val = Math.min(100, Math.max(0, Math.round(Number(raw))))
                                updateField('confirmationConfig', {
                                    ...bookingEngine.confirmationConfig,
                                    occupancyThreshold: val
                                })
                            }}
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            placeholder={'100'}
                            helperText={
                                bookingEngine.confirmationConfig?.occupancyThreshold !== undefined &&
                                (bookingEngine.confirmationConfig.occupancyThreshold < 0 || bookingEngine.confirmationConfig.occupancyThreshold > 100)
                                    ? t('occupancy_threshold_validation')
                                    : undefined
                            }
                            error={
                                bookingEngine.confirmationConfig?.occupancyThreshold !== undefined &&
                                (bookingEngine.confirmationConfig.occupancyThreshold < 0 || bookingEngine.confirmationConfig.occupancyThreshold > 100)
                            }
                        />
                    </Property>
                )}

                {/* Time slot duration */}
                <Property
                    title={t('Booking interval')}
                    subtitle={t('Sets how far apart the selectable time slots are for guests. For example, with 30 minutes: 5:00 PM, 5:30 PM, 6:00 PM.')}
                >
                    <Select
                        value={bookingEngine?.formConfig?.slotDurationMinutes || 30}
                        onChange={(e) => {
                            setBookingEngine({
                                ...bookingEngine,
                                formConfig: {
                                    ...bookingEngine.formConfig,
                                    slotDurationMinutes: e.target.value ? e.target.value as number : undefined
                                }
                            })
                        }}
                        fullWidth
                        size={'small'}
                        disabled={readOnly}
                        renderValue={(value) => value ? `${value} ${t('minutes')}` : t('None')}
                    >
                        {TIMESLOT_DURATION_MINUTES.map((duration) => (
                            <MenuItem
                                key={`slot-duration-${duration?.toString()}` || ''}
                                value={duration || ''}
                                id={`slot-duration-${duration}`}
                            >
                                {duration ? `${duration} ${t('minutes')}` : t('None')}
                            </MenuItem>
                        ))}
                    </Select>
                </Property>

                {/* Earliest bookable slot */}
                <Property
                    title={t('Earliest bookable slot')}
                    subtitle={t('Represents the earliest bookable slot from the current moment in hours')}
                >
                    <Select
                        value={bookingEngine?.formConfig?.earliestBookableSlotFromNowInHours || 0}
                        onChange={(e) => {
                            setBookingEngine({
                                ...bookingEngine,
                                formConfig: {
                                    ...bookingEngine.formConfig,
                                    earliestBookableSlotFromNowInHours: e.target.value as number
                                }
                            })
                        }}
                        fullWidth
                        size={'small'}
                        disabled={readOnly}
                        renderValue={(valueInHours) => {
                            if (valueInHours === 0) return t('Now');
                            if (valueInHours === 1) return `${valueInHours} ${t('hour from now')}`;
                            return `${valueInHours} ${t('hours from now')}`;
                        }}
                    >
                        {EARLIEST_BOOKABLE_SLOT_FROM_NOW_IN_HOURS.map((gapInHours) => (
                            <MenuItem
                                key={`time-gap-${gapInHours?.toString()}` || ''}
                                value={gapInHours || ''}
                                id={`time-gap-${gapInHours}`}
                            >
                                {gapInHours === 0
                                    ? t('Now')
                                    : gapInHours === 1
                                        ? `${gapInHours} ${t('hour')}`
                                        : `${gapInHours} ${t('hours')}`
                                }
                            </MenuItem>
                        ))}
                    </Select>
                </Property>

                {/* Custom Booking Window (Introduce time frame selection) */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Switch
                            id={'show-time-frame-selection'}
                            size={'small'}
                            disabled={readOnly}
                            checked={bookingEngine.formConfig?.timeFrameSelection || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                timeFrameSelection: e.target.checked,
                                startTimeFrame: undefined,
                                endTimeFrame: undefined
                            })}
                        />
                        <Box>
                            <Typography variant="body1" fontWeight={500}>
                                {t('Custom Booking Window')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t('Define when guests can book through the widget. Perfect for managing kitchen capacity and avoiding peak-time chaos.')}
                            </Typography>
                        </Box>
                    </Box>

                    {!!bookingEngine.formConfig?.timeFrameSelection && (
                        <Stack direction={'row'} gap={2} mt={2}>
                            <Datepicker
                                disabled={readOnly}
                                value={bookingEngine?.formConfig?.startTimeFrame}
                                defaultValue={new Date(new Date().setHours(0, 0, 0, 0))}
                                onChange={(event: any) => {
                                    const date = event.value
                                    const hours = date.getHours();
                                    const minutes = date.getMinutes();
                                    const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
                                    const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;
                                    const startTimeFrame = `${hoursString}:${minutesString}`;
                                    let endTimeFrame = bookingEngine.formConfig?.endTimeFrame
                                    if (startTimeFrame && startTimeFrame > endTimeFrame!) {
                                        endTimeFrame = '23:59'
                                    }
                                    updateField('formConfig', {
                                        ...bookingEngine.formConfig,
                                        startTimeFrame: startTimeFrame,
                                        endTimeFrame: endTimeFrame
                                    })
                                }}
                                label={t('Start time frame')}
                                theme={'material'}
                                themeVariant="light"
                                timeFormat={'HH:mm'}
                                labelStyle={'floating'}
                                inputStyle="outline"
                                responsive={{
                                    xsmall: { controls: ['time'], display: 'bottom', touchUi: true },
                                    small: { controls: ['time'], display: 'anchored', touchUi: true },
                                    custom: { breakpoint: 800, controls: ['time'], display: 'anchored', touchUi: false }
                                }}
                                firstDay={1}
                            />
                            <Datepicker
                                disabled={readOnly}
                                value={bookingEngine?.formConfig?.endTimeFrame}
                                defaultValue={new Date(new Date().setHours(0, 0, 0, 0))}
                                onChange={(event: any) => {
                                    const date = event.value
                                    const hours = date.getHours();
                                    const minutes = date.getMinutes();
                                    const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
                                    const minutesString = minutes < 10 ? `0${minutes}` : `${minutes}`;
                                    const endTimeFrame = `${hoursString}:${minutesString}`;
                                    let startTimeFrame = bookingEngine.formConfig?.startTimeFrame
                                    if (endTimeFrame && endTimeFrame < startTimeFrame!) {
                                        startTimeFrame = '00:00'
                                    }
                                    updateField('formConfig', {
                                        ...bookingEngine.formConfig,
                                        startTimeFrame: startTimeFrame,
                                        endTimeFrame: endTimeFrame
                                    })
                                }}
                                label={t('End time frame')}
                                theme={'material'}
                                themeVariant="light"
                                timeFormat={'HH:mm'}
                                labelStyle={'floating'}
                                inputStyle="outline"
                                responsive={{
                                    xsmall: { controls: ['time'], display: 'bottom', touchUi: true },
                                    small: { controls: ['time'], display: 'anchored', touchUi: true },
                                    custom: { breakpoint: 800, controls: ['time'], display: 'anchored', touchUi: false }
                                }}
                                firstDay={1}
                            />
                        </Stack>
                    )}
                </Box>
            </Stack>
        </SectionBox>
    )

    // ── Guest Information ────────────────────────────────────────────────────

    const guestInfoSection = (
        <SectionBox title={t('Guest Information')}>
            <Stack gap={1}>
                {/* Create or assign profile */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'create-profile'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.createOrAssignProfile || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                createOrAssignProfile: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Create or assign profile')}</Typography>
                            <InfoTooltip title={t('Create or assign profile widget info')} />
                        </Box>
                    }
                />

                {/* Phone mandatory */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'phone-mandatory'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.phoneMandatory || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                phoneMandatory: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Phone mandatory')}</Typography>
                            <InfoTooltip title={t('Requires guests to provide a phone number when booking')} />
                        </Box>
                    }
                />

                {/* Must accept privacy */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'accept-privacy'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.mustAcceptPrivacy || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                mustAcceptPrivacy: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Must accept privacy')}</Typography>
                            <InfoTooltip title={t('Requires guests to accept the privacy policy before completing a booking. To add a link, use the next html format: This is a test <a href="https://crqlar.com">LINK</a>')} />
                        </Box>
                    }
                />

                {!!bookingEngine.formConfig?.mustAcceptPrivacy && (
                    <Box sx={{ pl: 4 }}>
                        <Property title={t('Privacy policy text')}>
                            <LocalizedTextField
                                id={'nameLocalized'}
                                size={'small'}
                                value={bookingEngine.formConfig?.privacyPolicyText}
                                onChange={(e) => updateField('formConfig', {
                                    ...bookingEngine.formConfig,
                                    privacyPolicyText: e
                                })}
                                fullWidth
                                required
                                disabled={readOnly}
                            />
                        </Property>
                    </Box>
                )}

                {/* Optional customer detail fields */}
                <Divider sx={{ my: 1 }} />
                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1">{t('Optional customer fields')}</Typography>
                    <InfoTooltip title={t('Choose which address fields appear in the booking widget and whether each one is required')} />
                </Box>
                <Stack gap={0.5}>
                    {BOOKING_ENGINE_OPTIONAL_FIELDS.map((field) => {
                        const key = field.key as string
                        const config: OptionalFieldConfig = bookingEngine.formConfig?.optionalFields?.[key] || {}
                        const setConfig = (next: OptionalFieldConfig) => updateField('formConfig', {
                            ...bookingEngine.formConfig,
                            optionalFields: {
                                ...bookingEngine.formConfig?.optionalFields,
                                [key]: next
                            }
                        })
                        return (
                            <Stack key={key} direction="row" alignItems="center" justifyContent="space-between">
                                <FormControlLabel
                                    sx={{ mx: 0 }}
                                    disabled={readOnly}
                                    control={
                                        <Checkbox
                                            id={`optional-field-${key}`}
                                            size={'small'}
                                            checked={config.enabled || false}
                                            onChange={(e) => setConfig({
                                                enabled: e.target.checked,
                                                // Drop the mandatory flag when the field is disabled.
                                                mandatory: e.target.checked ? config.mandatory : false
                                            })}
                                        />
                                    }
                                    label={<Typography variant="body1">{t(field.value || key)}</Typography>}
                                />
                                {config.enabled && (
                                    <FormControlLabel
                                        sx={{ mx: 0 }}
                                        disabled={readOnly}
                                        control={
                                            <Checkbox
                                                id={`optional-field-${key}-mandatory`}
                                                size={'small'}
                                                checked={config.mandatory || false}
                                                onChange={(e) => setConfig({ ...config, mandatory: e.target.checked })}
                                            />
                                        }
                                        label={<Typography variant="body2">{t('Mandatory')}</Typography>}
                                    />
                                )}
                            </Stack>
                        )
                    })}
                </Stack>
            </Stack>
        </SectionBox>
    )

    // ── Payment / No-Show Settings ───────────────────────────────────────────

    const paymentSectionContent = availableCardProviders.length === 0 ? (
        <Stack gap={2} alignItems={'center'} textAlign={'center'}>
            <Typography variant="body1">
                {t('You first need to connect a payment provider (hobex or stripe)')}
            </Typography>
            <Stack direction={'row'} gap={1} alignItems={'center'}>
                <Button
                    variant={'outlined'}
                    size={'small'}
                    onClick={() => navigate('/marketplace')}
                >
                    {t('Connect stripe')}
                </Button>
                <Typography variant="body2">{t('or')}</Typography>
                <Button
                    variant={'outlined'}
                    size={'small'}
                    onClick={() => navigate('/marketplace')}
                >
                    {t('Connect hobex')}
                </Button>
            </Stack>
        </Stack>
    ) : (
        <Stack gap={2}>
            <Property title={t('Payment requirement')}>
                <Select
                    value={bookingEngine?.billingConfig?.paymentRequirement || ''}
                    onChange={(e) => {
                        setBookingEngine({
                            ...bookingEngine,
                            billingConfig: {
                                ...bookingEngine.billingConfig,
                                paymentRequirement: e.target.value ? e.target.value as PaymentRequirement : undefined
                            }
                        })
                    }}
                    fullWidth
                    size={'small'}
                    disabled={readOnly}
                    renderValue={(value) => value ? t(`Payment requirement ${value}`) : t('None')}
                >
                    {[undefined, ...Object.keys(PaymentRequirement)].map((requirementType) => (
                        <MenuItem
                            key={requirementType?.toString() || ''}
                            value={requirementType || ''}
                            id={`payment-requirement-${requirementType}`}
                        >
                            {requirementType ? t(`Payment requirement ${requirementType}`) : t('None')}
                        </MenuItem>
                    ))}
                </Select>
            </Property>

            {!!bookingEngine?.billingConfig?.paymentRequirement && (
                <>
                    <Divider/>
                    <Property title={t('Payment provider')}>
                        <Select
                            value={bookingEngine?.billingConfig?.provider || ''}
                            onChange={(e) => {
                                setBookingEngine({
                                    ...bookingEngine,
                                    billingConfig: {
                                        ...bookingEngine.billingConfig,
                                        provider: e.target.value ? e.target.value as string : undefined
                                    }
                                })
                            }}
                            fullWidth
                            size={'small'}
                            disabled={readOnly}
                        >
                            {[undefined, ...availableCardProviders].map((provider) => (
                                <MenuItem
                                    key={provider || ''}
                                    value={provider || ''}
                                    id={`sector-type-${provider}`}
                                >
                                    {provider ? provider : t('None')}
                                </MenuItem>
                            ))}
                        </Select>
                    </Property>

                    <Divider/>

                    <Property title={t('No show policy')}>
                        <LocalizedTextField
                            id={'noShowPolicy'}
                            size={'small'}
                            value={bookingEngine.billingConfig?.noShowPolicy}
                            onChange={(e) => updateField('billingConfig', {
                                ...bookingEngine.billingConfig,
                                noShowPolicy: e
                            })}
                            fullWidth
                            required
                            disabled={readOnly}
                        />
                    </Property>
                </>
            )}

            {bookingEngine.billingConfig?.paymentRequirement !== PaymentRequirement.UPFRONT_PAYMENT && (
                <>
                    <FormControlLabel
                        sx={{ mx: 0 }}
                        disabled={readOnly}
                        control={
                            <Checkbox
                                id={'charge-per-person'}
                                size={'small'}
                                checked={bookingEngine.billingConfig?.showChargePerPerson || false}
                                onChange={(e) => updateField('billingConfig', {
                                    ...bookingEngine.billingConfig,
                                    showChargePerPerson: e.target.checked,
                                    chargePerPersonAmount: undefined,
                                    minNumberOfPeopleForCharge: undefined
                                })}
                            />
                        }
                        label={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1">{t('Charge per person')}</Typography>
                                <InfoTooltip title={t('Default amount charged per person for no-show')} />
                            </Box>
                        }
                    />

                    {!!bookingEngine.billingConfig?.showChargePerPerson && (
                        <Stack direction={'row'} gap={2} alignItems={'center'}>
                            <CountTextField
                                id={'default-charge'}
                                type={'number'}
                                label={t('Default charge')}
                                size={'small'}
                                disabled={readOnly}
                                value={bookingEngine.billingConfig.chargePerPersonAmount}
                                onChangeValue={(e) => {
                                    const nonNegativeValue = Math.max(0, Number(e))
                                    updateField('billingConfig', {
                                        ...bookingEngine.billingConfig,
                                        chargePerPersonAmount: nonNegativeValue
                                    })
                                }}
                                fullWidth
                                required
                            />
                            <CountTextField
                                id={'min-number-of-people'}
                                type={'number'}
                                label={t('Min guest num to ask for a fee')}
                                size={'small'}
                                disabled={readOnly}
                                value={bookingEngine.billingConfig.minNumberOfPeopleForCharge}
                                onChangeValue={(e) => {
                                    const nonNegativeValue = Math.max(0, Number(e))
                                    updateField('billingConfig', {
                                        ...bookingEngine.billingConfig,
                                        minNumberOfPeopleForCharge: nonNegativeValue
                                    })
                                }}
                                fullWidth
                                required
                            />
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    )

    const paymentSection = (
        <SectionBox
            title={t('Payment/No-Show Settings')}
            toggleable
            expanded={paymentExpanded}
            onToggle={setPaymentExpanded}
        >
            {paymentSectionContent}
        </SectionBox>
    )

    // ── Advanced Settings ────────────────────────────────────────────────────

    const advancedSettingsSection = (
        <SectionBox
            title={t('Advanced Settings')}
            toggleable
            expanded={advancedExpanded}
            onToggle={setAdvancedExpanded}
        >
            <Stack gap={1}>
                {/* Turn on / off time selection */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'show-time-selection'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.timeSelection || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                timeSelection: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Turn on / off time selection')}</Typography>
                            <InfoTooltip title={t('Controls whether guests can select specific time slots (e.g., 6:30 PM) when making a reservation. Example: In a day spa, guests don\'t need a time slot because they can visit the day spa area during general opening hours.')} />
                        </Box>
                    }
                />

                {/* People limit */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'people-limit'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.peopleLimitEnabled || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                peopleLimitEnabled: e.target.checked,
                                peopleLimitNumber: e.target.checked ? 1 : undefined
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Max. People limit')}</Typography>
                            <InfoTooltip title={t('This sets the maximum number of guests allowed in a single reservation. Recommended for group reservations, e.g., a maximum of 8 people in a restaurant.')} />
                        </Box>
                    }
                />

                {!!bookingEngine.formConfig?.peopleLimitEnabled && (
                    <Box sx={{ pl: 4 }}>
                        <Stack direction={'row'} maxWidth={'300px'} gap={2}>
                            <CountTextField
                                id={'people-limit-number'}
                                type={'number'}
                                label={t('Max people')}
                                size={'small'}
                                disabled={readOnly}
                                value={bookingEngine.formConfig.peopleLimitNumber}
                                onChangeValue={(e) => {
                                    const nonNegativeValue = Math.max(1, Number(e))
                                    updateField('formConfig', {
                                        ...bookingEngine.formConfig,
                                        peopleLimitNumber: nonNegativeValue || 1
                                    })
                                }}
                                fullWidth
                                required
                            />
                        </Stack>
                    </Box>
                )}

                {/* Only show availabilities where service has rate */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={readOnly}
                    control={
                        <Checkbox
                            id={'service-rate-mandatory'}
                            size={'small'}
                            checked={bookingEngine.formConfig?.serviceRateMandatory || false}
                            onChange={(e) => updateField('formConfig', {
                                ...bookingEngine.formConfig,
                                serviceRateMandatory: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Only show availabilities where service has a rate')}</Typography>
                            <InfoTooltip title={t('Displays only time slots for which the service has a price listed. Example: Activate if treatments in the spa area have prices listed. Deactivate if the service in the restaurant has no price listed.')} />
                        </Box>
                    }
                />

                {/* Allow multiple reservations */}
                <FormControlLabel
                    sx={{ mx: 0 }}
                    disabled={hasPackageService || readOnly}
                    control={
                        <Checkbox
                            id={'multi-res'}
                            size={'small'}
                            checked={bookingEngine.cartConfig?.allowMultipleRes || false}
                            disabled={hasPackageService}
                            onChange={(e) => updateField('cartConfig', {
                                ...bookingEngine.cartConfig,
                                allowMultipleRes: e.target.checked
                            })}
                        />
                    }
                    label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1">{t('Allow multiple services per booking')}</Typography>
                            <InfoTooltip title={t('Allows guests to book multiple services in one transaction. For example, they can add several spa treatments without restarting the booking process.')} />
                        </Box>
                    }
                />

                {hasPackageService && (
                    <Alert severity={'warning'}>
                        {t('Multiple reservation functionality is not available when one of the services offered is service package.')}
                    </Alert>
                )}
            </Stack>
        </SectionBox>
    )

    return (
        <Stack gap={2}>
            {basicInfoSection}
            {bookingTimesSection}
            {guestInfoSection}
            {paymentSection}
            {advancedSettingsSection}
            <Box sx={{mt: 3}}>
                <Typography variant="body2" color="text.secondary" textAlign="start">
                    {t('Last updated')}: {bookingEngine.updateDate
                        ? formatFullDateTime(bookingEngine.updateDate)
                        : '-'}
                </Typography>
            </Box>
        </Stack>
    )
}

export default BookingEngineBasicTab;
